// Participant-facing copy for the public registration, Stripe payment and email confirmation pages.
// Public pages choose their language from `?lang=` (hr or en); de and it fall back to hr through i18next.
// `{name}` placeholders are filled by the page helpers, not by i18next interpolation.

const publicRegistrationEn = {
    pageTitle: "Registration",
    language: "Language",
    eventDates: "Dates",
    eventPlace: "Place",
    eventMaterials: "Event materials",
    combinedEventMaterials: "Program and venue PDF",
    programPdf: "Program",
    venueLayout: "Venue map",
    whatHappensNext: "What happens after you submit",
    submitCreatesRegistration: "Submit creates your registration.",
    emailConfirmationVerifies: "Email confirmation verifies your contact address for this registration.",
    offersAfterConfirmation: "Offers are sent after confirmation when payment is needed.",
    noFeeAfterConfirmation: "After email confirmation, ATI has the registration details; no offer is sent unless payment is needed.",
    roomReservedBeforePayment: "A selected room is reserved immediately on submit, before payment.",
    loading: "Loading registration form",
    loadError: "The registration form could not be loaded.",
    registrationUnavailableTitle: "Registration is no longer available",
    registrationFormInactiveTitle: "Registration is not available yet",
    registrationUnavailableArchived: "This event has been archived. New registrations are no longer possible.",
    registrationUnavailableCompleted: "This event has been marked as completed. New registrations are no longer possible.",
    registrationUnavailableEnded: "The event has ended. New registrations are no longer possible.",
    registrationUnavailableFormInactive: "The registration form has not been activated yet. Please try again later.",
    submitError: "The registration could not be submitted.",
    duplicateRegistrationEmail: "A registration with this email address already exists for this event.",
    requiredFieldsError: "Check the required fields and try again.",
    invalidValuesError: "Check the entered values and try again.",
    missingCode: "Event link is missing.",
    participant: "Participant",
    participantHelp: "Enter the basic details for the person registering.",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    phoneNumber: "Phone",
    company: "Institution / company",
    linkedInUrl: "LinkedIn (optional)",
    invalidLinkedInUrl: "Enter a valid LinkedIn URL.",
    payer: "Payer",
    payerHelp: "Choose who receives and pays the offer. Only the fields needed for that payer type are shown.",
    payerType: "Payer type",
    individual: "Personal",
    companyPayer: "Company / institution",
    sponsoredParticipation: "Sponsored participation",
    individualPayerDescription: "The participant pays personally. Tax id and payer address are required.",
    companyPayerDescription: "A company or institution pays. Organization details and a payment contact are required.",
    sponsoredPayerDescription: "A sponsor covers participation. Sponsor details and a payment contact are required.",
    payerName: "Payer name",
    payerEmail: "Payer email",
    payerPhone: "Payer phone",
    payerAddress: "Payer address",
    payerCity: "City",
    payerCountry: "Country",
    payerTaxId: "Tax id",
    payerContactPerson: "Payer contact person",
    registrationQuestions: "Additional questions",
    items: "Participation and optional items",
    itemsHelp: "Choose the offered items. Amounts come from the event price list.",
    participationFee: "Participation fee type",
    participationFeeMissing: "Paid registration is not available yet because participation prices are not ready. Please try again later.",
    participantTypeProof: "Participant type proof",
    participantTypeProofHelp: "Upload a PDF or image that confirms the selected participation fee type.",
    participantTypeProofUpload: "Upload proof",
    participantTypeProofUploading: "Uploading proof",
    participantTypeProofUploaded: "Proof uploaded",
    participantTypeProofRequired: "Upload participant type proof.",
    participantTypeProofUploadFailed: "The proof file could not be uploaded.",
    bulkProofNotSupported: "The selected participation fee type requires proof per registration. Use individual registration.",
    additionalOptions: "Additional options",
    noPaidOptions: "No additional paid options are configured.",
    accommodationSection: "Accommodation",
    accommodationHelp: "Accommodation is optional. If you choose a room, it is reserved immediately when you submit, before payment.",
    reserveAccommodation: "Reserve accommodation",
    accommodationChoice: "Hotel and room type",
    accommodationReservationNotice: "Your selected room is reserved immediately when you submit, before payment.",
    accommodationOfferNotice: "Accommodation offer is separate and is sent after email confirmation.",
    arrivalDate: "Arrival date",
    departureDate: "Departure date",
    roomCount: "Room count",
    roomPerNight: "night",
    roomsAvailable: "available",
    accommodationEstimate: "Accommodation estimate",
    noRoomsRemaining: "There are no rooms left for {date}. Choose different dates or remove accommodation from the registration.",
    roomsRemaining: "Rooms available for {date}: {remaining}. Reduce the room count or choose different dates.",
    participationPaymentMethod: "Participation/options payment method",
    accommodationPaymentMethod: "Accommodation payment method",
    missingPaymentMethods: "No payment methods are configured for this category.",
    missingParticipationPaymentMethods: "The payment method for participation/options is not available yet. Please try again later.",
    missingAccommodationPaymentMethods: "The payment method for accommodation is not available yet. Please try again later.",
    submit: "Submit registration",
    required: "Required field",
    invalidEmail: "Enter a valid email address.",
    atLeastOneItem: "Add at least a participation/options item or an accommodation reservation.",
    submittedTitle: "Registration received",
    submittedSubTitle: "Your registration has been created. Check your email and confirm your address; after confirmation, offers are sent to the payer when payment is needed.",
    submittedSubTitleNoPayment: "Your registration has been created. Check your email and confirm your address so ATI can verify the contact details; no offer is sent because this registration has no payment items.",
    submissionSummary: "Registration summary",
    selectedItems: "Selected items",
    noSelectedItems: "No payment items.",
    totalAmount: "Total",
    submitFailedTitle: "Registration not submitted",
    validationErrorSummary: "The registration was not submitted. Review the first highlighted field.",
    verificationExpires: "Verification link expires at",
    resendConfirmationEmail: "Resend confirmation email",
    resendingConfirmationEmail: "Sending confirmation",
    confirmationResent: "Confirmation email has been sent again.",
    confirmationResendFailed: "The confirmation email could not be sent again.",
    backToForm: "Start another registration",
    bankTransfer: "Bank transfer",
    card: "Card (Stripe)",
    sponsor: "Sponsor",
    pos: "Pay at registration",
    cash: "Cash",
    privacyConsent: "I accept the privacy notice and agree that ATI can process these registration details for this event.",
    privacyConsentRequired: "Accept the privacy notice to continue.",
    registrationMode: "Registration mode",
    individualRegistrationTab: "Individual registration",
    bulkRegistrationTab: "Group entry",
    bulkScopeTitle: "Advanced group entry",
    bulkScopeNotice: "Use this only when several participants share one payer and one payment setup. If you need accommodation, continue through individual registration.",
    bulkSharedSettings: "Shared payer and payment",
    bulkSharedSettingsHelp: "Enter the payer details and payment method once for the whole group registration.",
    bulkParticipantRows: "Participants",
    bulkParticipantRowsHelp: "Add one row per participant. Each row includes the same basic participant details as individual registration.",
    bulkCsvTools: "CSV tools",
    downloadBulkCsvTemplate: "Download CSV template",
    importBulkCsv: "Import populated CSV",
    bulkImportSuccess: "Imported rows: {count}.",
    bulkImportEmpty: "The CSV does not contain rows to import.",
    bulkImportDataOnMarkerRow: "Participant details were entered on the instruction row (EVENTFLOW_DATA_START). Move them to a new row below and enter the participant’s first name in the first column. Then import the CSV again. Your existing entries have not changed.",
    bulkImportFailed: "The CSV could not be imported.",
    bulkImportFormatError: "CSV format is not recognized. Use the downloaded EventFlow template and save it as comma-, semicolon-, or tab-delimited CSV.",
    bulkImportSkippedColumns: "Unknown or unsupported columns",
    bulkRow: "Participant",
    addBulkRow: "Add participant",
    removeBulkRow: "Remove participant",
    deleteConfirmationTitle: "Confirm deletion",
    deleteBulkRowTarget: "The participant row will be deleted",
    deleteConfirmationQuestion: "Are you sure you want to delete it?",
    deleteConfirmationCancel: "Cancel",
    validateBulkRows: "Validate rows",
    bulkValidationPassed: "Rows and shared settings passed validation.",
    bulkValidationFailed: "Review the highlighted rows and shared settings.",
    submitBulkRows: "Submit bulk registrations",
    bulkSubmitSuccess: "Accepted rows: {accepted}. Registrations were created; each participant must confirm their email before offers are sent.",
    bulkSubmitSuccessNoPayment: "Accepted rows: {accepted}. Registrations were created; each participant must confirm their email, and no offers are sent unless payment is needed.",
    bulkSubmitPartialFailure: "Accepted: {accepted}; rejected: {rejected}.",
    bulkSubmitFailed: "No rows were accepted.",
    bulkSubmitResults: "Submission results",
    bulkSubmitAccepted: "Accepted",
    bulkSubmitRejected: "Rejected",
    invalidNumber: "Enter a number.",
    invalidDate: "Enter a valid date.",
    invalidPhone: "Enter a valid phone number.",
    invalidSelection: "Choose an allowed value.",
    invalidPaymentMethod: "Choose a configured payment method."
};

const publicRegistrationHr: Record<keyof typeof publicRegistrationEn, string> = {
    pageTitle: "Registracija",
    language: "Jezik",
    eventDates: "Termin",
    eventPlace: "Mjesto",
    eventMaterials: "Materijali događaja",
    combinedEventMaterials: "Program i tlocrt",
    programPdf: "Program",
    venueLayout: "Tlocrt dvorane",
    whatHappensNext: "Što se događa nakon slanja",
    submitCreatesRegistration: "Slanje odmah stvara prijavu.",
    emailConfirmationVerifies: "Potvrda e-maila provjerava kontakt adresu za ovu prijavu.",
    offersAfterConfirmation: "Ponude se šalju nakon potvrde ako prijava ima stavke za plaćanje.",
    noFeeAfterConfirmation: "Nakon potvrde e-maila ATI ima podatke prijave; ponuda se ne šalje ako nema stavki za plaćanje.",
    roomReservedBeforePayment: "Odabrana soba rezervira se odmah pri slanju, prije plaćanja.",
    loading: "Učitavanje obrasca za registraciju",
    loadError: "Obrazac za registraciju nije moguće učitati.",
    registrationUnavailableTitle: "Prijava više nije dostupna",
    registrationFormInactiveTitle: "Prijava još nije dostupna",
    registrationUnavailableArchived: "Događaj je arhiviran. Nove prijave više nisu moguće.",
    registrationUnavailableCompleted: "Događaj je označen kao završen. Nove prijave više nisu moguće.",
    registrationUnavailableEnded: "Događaj je završio. Nove prijave više nisu moguće.",
    registrationUnavailableFormInactive: "Prijavni obrazac još nije aktiviran. Pokušajte ponovno kasnije.",
    submitError: "Prijavu nije moguće poslati.",
    duplicateRegistrationEmail: "Prijava s ovom e-mail adresom već postoji za ovaj događaj.",
    requiredFieldsError: "Provjerite obavezna polja i pokušajte ponovno.",
    invalidValuesError: "Provjerite unesene vrijednosti i pokušajte ponovno.",
    missingCode: "Nedostaje poveznica događaja.",
    participant: "Sudionik",
    participantHelp: "Unesite osnovne podatke osobe koja se prijavljuje.",
    firstName: "Ime",
    lastName: "Prezime",
    email: "E-mail",
    phoneNumber: "Telefon",
    company: "Institucija / tvrtka",
    linkedInUrl: "LinkedIn (opcionalno)",
    invalidLinkedInUrl: "Unesite ispravnu LinkedIn poveznicu.",
    payer: "Platitelj",
    payerHelp: "Odaberite tko prima i plaća ponudu. Prikazuju se samo potrebna polja za taj odabir.",
    payerType: "Vrsta platitelja",
    individual: "Osobno",
    companyPayer: "Tvrtka / ustanova",
    sponsoredParticipation: "Sponzorirano sudjelovanje",
    individualPayerDescription: "Sudionik sam plaća. Potrebni su OIB i adresa platitelja.",
    companyPayerDescription: "Račun plaća tvrtka ili ustanova. Potrebni su podaci organizacije i kontakt za plaćanje.",
    sponsoredPayerDescription: "Sudjelovanje pokriva sponzor. Potrebni su podaci sponzora i kontakt za plaćanje.",
    payerName: "Naziv platitelja",
    payerEmail: "E-mail platitelja",
    payerPhone: "Telefon platitelja",
    payerAddress: "Adresa platitelja",
    payerCity: "Grad",
    payerCountry: "Država",
    payerTaxId: "OIB / porezni broj",
    payerContactPerson: "Kontakt osoba platitelja",
    registrationQuestions: "Dodatna pitanja",
    items: "Kotizacija i dodatne opcije",
    itemsHelp: "Odaberite ponuđene stavke. Iznosi se računaju iz cjenika događaja.",
    participationFee: "Vrsta kotizacije",
    participationFeeMissing: "Prijava s kotizacijom trenutno nije dostupna jer cijene još nisu spremne. Pokušajte ponovno kasnije.",
    participantTypeProof: "Dokaz o vrsti sudionika",
    participantTypeProofHelp: "Prenesite PDF ili sliku koja potvrđuje odabranu vrstu kotizacije.",
    participantTypeProofUpload: "Prenesi dokaz",
    participantTypeProofUploading: "Prijenos dokaza",
    participantTypeProofUploaded: "Dokaz je prenesen",
    participantTypeProofRequired: "Prenesite dokaz o vrsti sudionika.",
    participantTypeProofUploadFailed: "Dokaz nije moguće prenijeti.",
    bulkProofNotSupported: "Odabrana vrsta kotizacije traži dokaz po prijavi. Koristite pojedinačnu prijavu.",
    additionalOptions: "Dodatne opcije",
    noPaidOptions: "Nema konfiguriranih dodatnih plaćenih opcija.",
    accommodationSection: "Smještaj",
    accommodationHelp: "Smještaj je opcionalan. Ako ga odaberete, soba se zauzima odmah pri slanju prijave, prije plaćanja.",
    reserveAccommodation: "Želim rezervirati smještaj",
    accommodationChoice: "Hotel i tip sobe",
    accommodationReservationNotice: "Odabrana soba rezervira se odmah pri slanju prijave, prije plaćanja.",
    accommodationOfferNotice: "Ponuda za smještaj je odvojena i šalje se nakon potvrde e-maila.",
    arrivalDate: "Datum dolaska",
    departureDate: "Datum odlaska",
    roomCount: "Broj soba",
    roomPerNight: "noćenje",
    roomsAvailable: "slobodno",
    accommodationEstimate: "Procjena smještaja",
    noRoomsRemaining: "Za {date} više nema slobodnih soba. Odaberite drugi termin ili uklonite smještaj iz prijave.",
    roomsRemaining: "Broj slobodnih soba za {date}: {remaining}. Smanjite broj soba ili odaberite drugi termin.",
    participationPaymentMethod: "Način plaćanja kotizacije/opcija",
    accommodationPaymentMethod: "Način plaćanja smještaja",
    missingPaymentMethods: "Za ovu kategoriju nisu konfigurirani načini plaćanja.",
    missingParticipationPaymentMethods: "Način plaćanja za kotizaciju/opcije trenutno nije dostupan. Pokušajte ponovno kasnije.",
    missingAccommodationPaymentMethods: "Način plaćanja za smještaj trenutno nije dostupan. Pokušajte ponovno kasnije.",
    submit: "Pošalji prijavu",
    required: "Obavezno polje",
    invalidEmail: "Unesite ispravnu e-mail adresu.",
    atLeastOneItem: "Dodajte barem stavku kotizacije/opcija ili rezervaciju smještaja.",
    submittedTitle: "Prijava je zaprimljena",
    submittedSubTitle: "Prijava je stvorena. Provjerite e-mail i potvrdite adresu; nakon potvrde sustav šalje ponude platitelju kada je plaćanje potrebno.",
    submittedSubTitleNoPayment: "Prijava je stvorena. Provjerite e-mail i potvrdite adresu kako bi ATI potvrdio kontakt podatke; ponuda se ne šalje jer prijava nema stavki za plaćanje.",
    submissionSummary: "Pregled prijave",
    selectedItems: "Odabrane stavke",
    noSelectedItems: "Nema stavki za plaćanje.",
    totalAmount: "Ukupno",
    submitFailedTitle: "Prijava nije poslana",
    validationErrorSummary: "Prijava nije poslana. Provjerite prvo označeno polje.",
    verificationExpires: "Poveznica za potvrdu vrijedi do",
    resendConfirmationEmail: "Ponovno pošalji e-mail potvrde",
    resendingConfirmationEmail: "Slanje potvrde",
    confirmationResent: "E-mail potvrde ponovno je poslan.",
    confirmationResendFailed: "E-mail potvrde nije moguće ponovno poslati.",
    backToForm: "Započni novu prijavu",
    bankTransfer: "Bankovna uplata",
    card: "Kartica (Stripe)",
    sponsor: "Sponzor",
    pos: "Plaćanje na registraciji",
    cash: "Gotovina",
    privacyConsent: "Prihvaćam obavijest o privatnosti i suglasan/na sam da ATI obradi podatke prijave za ovaj događaj.",
    privacyConsentRequired: "Prihvatite obavijest o privatnosti za nastavak.",
    registrationMode: "Način prijave",
    individualRegistrationTab: "Pojedinačna prijava",
    bulkRegistrationTab: "Skupni unos",
    bulkScopeTitle: "Napredni skupni unos",
    bulkScopeNotice: "Koristite ga samo kada više sudionika dijeli istog platitelja i isti način plaćanja. Ako trebate smještaj, nastavite kroz pojedinačnu prijavu.",
    bulkSharedSettings: "Zajednički platitelj i plaćanje",
    bulkSharedSettingsHelp: "Unesite podatke o platitelju i način plaćanja jednom za cijelu skupnu prijavu.",
    bulkParticipantRows: "Sudionici",
    bulkParticipantRowsHelp: "Dodajte jedan red po sudioniku. Svaki red sadrži iste osnovne podatke kao pojedinačna prijava.",
    bulkCsvTools: "CSV alati",
    downloadBulkCsvTemplate: "Preuzmi CSV predložak",
    importBulkCsv: "Uvezi popunjeni CSV",
    bulkImportSuccess: "Uvezeno redova: {count}.",
    bulkImportEmpty: "CSV ne sadrži redove za uvoz.",
    bulkImportDataOnMarkerRow: "Podaci sudionika uneseni su u redak s uputama (EVENTFLOW_DATA_START). Premjestite ih u novi redak ispod i upišite ime sudionika u prvi stupac. Zatim ponovno uvezite CSV. Postojeći unos nije promijenjen.",
    bulkImportFailed: "CSV nije moguće uvesti.",
    bulkImportFormatError: "CSV format nije prepoznat. Koristite preuzeti EventFlow predložak i spremite ga kao CSV odvojen zarezom, točka-zarezom ili tabulatorom.",
    bulkImportSkippedColumns: "Nepoznati ili nepodržani stupci",
    bulkRow: "Sudionik",
    addBulkRow: "Dodaj sudionika",
    removeBulkRow: "Ukloni sudionika",
    deleteConfirmationTitle: "Potvrda brisanja",
    deleteBulkRowTarget: "Briše se redak sudionika",
    deleteConfirmationQuestion: "Jeste li sigurni da želite obrisati?",
    deleteConfirmationCancel: "Odustani",
    validateBulkRows: "Provjeri redove",
    bulkValidationPassed: "Redovi i zajedničke postavke su ispravni.",
    bulkValidationFailed: "Provjerite označene redove i zajedničke postavke.",
    submitBulkRows: "Pošalji skupne prijave",
    bulkSubmitSuccess: "Prihvaćeno redova: {accepted}. Prijave su stvorene; svaki sudionik mora potvrditi e-mail prije slanja ponuda.",
    bulkSubmitSuccessNoPayment: "Prihvaćeno redova: {accepted}. Prijave su stvorene; svaki sudionik mora potvrditi e-mail, a ponude se ne šalju ako nema stavki za plaćanje.",
    bulkSubmitPartialFailure: "Prihvaćeno: {accepted}; odbijeno: {rejected}.",
    bulkSubmitFailed: "Nijedan red nije prihvaćen.",
    bulkSubmitResults: "Rezultati slanja",
    bulkSubmitAccepted: "Zaprimljeno",
    bulkSubmitRejected: "Odbijeno",
    invalidNumber: "Unesite broj.",
    invalidDate: "Unesite ispravan datum.",
    invalidPhone: "Unesite ispravan telefon.",
    invalidSelection: "Odaberite dopuštenu vrijednost.",
    invalidPaymentMethod: "Odaberite konfigurirani način plaćanja."
};

const publicPaymentEn = {
    title: "Offer payment",
    language: "Language",
    paid: "Payment confirmed",
    pending: "Payment confirmation is pending",
    unavailable: "Payment is unavailable",
    help: "Check the email link or contact the organizer for a new link.",
    pendingHelp: "Confirmation comes from the payment provider. Returning to this page alone does not confirm payment.",
    review: "Payment needs review",
    reviewHelp: "Contact the organizer before making another payment.",
    cancelled: "You returned from checkout. If you did not pay, you can open checkout again.",
    pay: "Pay with Stripe",
    refresh: "Check status",
    remaining: "Remaining amount",
    refunded: "Refunded",
    participation: "Participation and options",
    accommodation: "Accommodation",
    failed: "Checkout could not be opened. Try again or contact the organizer.",
    statusCheckFailed: "Payment status could not be checked with Stripe. The last known status is shown. Please try again.",
    secure: "Stripe handles checkout. Card details are not stored in this application."
};

const publicPaymentHr: Record<keyof typeof publicPaymentEn, string> = {
    title: "Plaćanje ponude",
    language: "Jezik",
    paid: "Plaćanje je potvrđeno",
    pending: "Čekamo potvrdu plaćanja",
    unavailable: "Plaćanje nije dostupno",
    help: "Provjerite poveznicu iz e-maila ili se obratite organizatoru za novu poveznicu.",
    pendingHelp: "Potvrda dolazi od pružatelja plaćanja. Povratak na ovu stranicu sam po sebi ne potvrđuje uplatu.",
    review: "Plaćanje zahtijeva provjeru",
    reviewHelp: "Obratite se organizatoru prije novog plaćanja.",
    cancelled: "Vratili ste se iz naplate. Ako niste platili, možete ponovno otvoriti naplatu.",
    pay: "Platite putem Stripea",
    refresh: "Provjerite status",
    remaining: "Preostalo za uplatu",
    refunded: "Vraćeno",
    participation: "Kotizacija i opcije",
    accommodation: "Smještaj",
    failed: "Naplatu nije moguće otvoriti. Pokušajte ponovno ili se obratite organizatoru.",
    statusCheckFailed: "Status plaćanja nije moguće provjeriti kod Stripea. Prikazan je zadnji poznati status. Pokušajte ponovno.",
    secure: "Naplatom upravlja Stripe. Podaci o kartici ne pohranjuju se u ovoj aplikaciji."
};

const publicEmailConfirmationEn = {
    loadingTitle: "Confirming your email address",
    loadingHelp: "Please wait while we check the link from your email.",
    successTitle: "Your email address has been confirmed",
    successHelp: "Thank you. The contact address for this registration has been confirmed.",
    alreadyTitle: "The email address is already confirmed",
    alreadyHelp: "This link has already been used. No further action is needed.",
    expiredTitle: "The confirmation link has expired",
    expiredHelp: "Request a new registration confirmation email.",
    invalidTitle: "The confirmation link is invalid",
    invalidHelp: "Check that you opened the complete link from the email.",
    genericTitle: "The email address could not be confirmed",
    genericHelp: "Try again or request a new registration confirmation email.",
    returnToRegistration: "Return to registration",
    viewRegistrationDetails: "Back to registration details"
};

const publicEmailConfirmationHr: Record<keyof typeof publicEmailConfirmationEn, string> = {
    loadingTitle: "Potvrđujemo e-mail adresu",
    loadingHelp: "Molimo pričekajte dok provjeravamo poveznicu iz e-maila.",
    successTitle: "Vaša e-mail adresa je potvrđena",
    successHelp: "Hvala. Kontaktna adresa za ovu prijavu uspješno je potvrđena.",
    alreadyTitle: "E-mail adresa već je potvrđena",
    alreadyHelp: "Ova poveznica već je iskorištena. Nije potrebna dodatna radnja.",
    expiredTitle: "Poveznica je istekla",
    expiredHelp: "Zatražite novi e-mail za potvrdu prijave.",
    invalidTitle: "Poveznica nije ispravna",
    invalidHelp: "Provjerite jeste li otvorili cijelu poveznicu iz e-maila.",
    genericTitle: "E-mail nije moguće potvrditi",
    genericHelp: "Pokušajte ponovno ili zatražite novi e-mail za potvrdu prijave.",
    returnToRegistration: "Povratak na prijavu",
    viewRegistrationDetails: "Povratak na detalje prijave"
};

export type PublicRegistrationLabelKey = keyof typeof publicRegistrationEn;
export type PublicPaymentLabelKey = keyof typeof publicPaymentEn;
export type PublicEmailConfirmationLabelKey = keyof typeof publicEmailConfirmationEn;

export const publicRegistrationLabelKeys = Object.keys(publicRegistrationEn) as PublicRegistrationLabelKey[];
export const publicPaymentLabelKeys = Object.keys(publicPaymentEn) as PublicPaymentLabelKey[];
export const publicEmailConfirmationLabelKeys = Object.keys(publicEmailConfirmationEn) as PublicEmailConfirmationLabelKey[];

const withPrefix = (prefix: string, labels: Record<string, string>): Record<string, string> =>
    Object.fromEntries(Object.entries(labels).map(([key, value]) => [`${prefix}.${key}`, value]));

export const publicPagesEn: Record<string, string> = {
    ...withPrefix("public-registration", publicRegistrationEn),
    ...withPrefix("public-payment", publicPaymentEn),
    ...withPrefix("public-email-confirmation", publicEmailConfirmationEn)
};

export const publicPagesHr: Record<string, string> = {
    ...withPrefix("public-registration", publicRegistrationHr),
    ...withPrefix("public-payment", publicPaymentHr),
    ...withPrefix("public-email-confirmation", publicEmailConfirmationHr)
};
